'use client'

import { ReactNode } from 'react'

import { useQuery } from '@tanstack/react-query'

import { LotAssertionHistory } from '~/app/accounting/LotAssertionHistory'
import { directionForChange, EMPTY_VALUE } from '~/app/accounting/display-utils'
import { LotRow } from '~/app/accounting/lot-rows'
import {
  CurrencyAmount,
  LoadingSpinner,
  tokenBalanceFormatters,
} from '~/components/component-library'
import { AddressDisplay } from '~/components/component-library/AddressDisplay'
import { SplitsDialogContent } from '~/components/component-library/Dialog'
import { LabelValue } from '~/components/component-library/LabelValue'
import { SupportedChainId } from '~/constants/chains'
import { QUERY_KEYS } from '~/constants/react-query'
import { useActiveOrg } from '~/hooks/org'
import { useAccountingTimezone } from '~/hooks/useAccountingTimezone'
import { useServerRequests } from '~/hooks/useRequests'
import { useTokenPrices } from '~/hooks/useTokenPrices'
import { IAddress } from '~/types'
import { getNumberFromBigNumber } from '~/utils/numbers'
import { formatWithLocalTimezone } from '~/utils/time'

// Absolute with the time of day, so same-day trades read apart.
const DATE_TIME_FORMAT = 'DD MMM YYYY [at] h:mm A'

type LotDisposal = {
  id: string
  chainId: number
  tokenAddress: string
  quantity: string
  costBasis: string
  proceeds: string | null
  realizedChange: string | null
  disposalKind: string
  disposalType: string | null
  warnings: string[]
  disposalTime: string
  transactionHash: string | null
  counterpartyAddress: string | null
}

const DISPOSAL_KIND_VERBS: Record<string, string> = {
  external: 'Sold',
  internal_transfer: 'Moved internally',
  bridge: 'Bridged',
}

const DetailRow = ({ label, value }: { label: string; value: ReactNode }) => (
  <div className="flex items-start justify-between gap-2 text-sm">
    <span className="text-secondary">{label}</span>
    <span className="flex min-w-0 justify-end text-right">{value}</span>
  </div>
)

const DisposalRow = ({
  disposal,
  lot,
}: {
  disposal: LotDisposal
  lot: LotRow
}) => {
  const [tz] = useAccountingTimezone()
  const quantity = getNumberFromBigNumber(
    BigInt(disposal.quantity),
    lot.decimals,
  )
  const amount = tokenBalanceFormatters.formatAbbreviatedTokenAmount(quantity, {
    address: lot.tokenAddress as IAddress,
    chainId: lot.chainId,
  })
  const verb = DISPOSAL_KIND_VERBS[disposal.disposalKind] ?? 'Disposed'
  const proceeds = disposal.proceeds != null ? Number(disposal.proceeds) : null
  const gain =
    disposal.realizedChange != null ? Number(disposal.realizedChange) : null

  return (
    <div className="px-4 py-3">
      <LabelValue
        size="small"
        label={`${verb} ${amount}`}
        value={
          <span className="text-secondary">
            {formatWithLocalTimezone(new Date(disposal.disposalTime), {
              format: DATE_TIME_FORMAT,
              tz,
            })}
          </span>
        }
        isCollapsible
        defaultOpen={false}
      >
        <div className="flex flex-col gap-2.5 pt-1">
          <DetailRow
            label="Counterparty"
            value={
              disposal.counterpartyAddress ? (
                <AddressDisplay
                  address={disposal.counterpartyAddress as IAddress}
                  layout="standard"
                  size="small"
                />
              ) : (
                EMPTY_VALUE
              )
            }
          />
          <DetailRow
            label="Value out"
            value={
              proceeds != null ? (
                <div className="flex flex-col items-end">
                  <CurrencyAmount
                    layout="standard"
                    size="small"
                    value={proceeds}
                  />
                  <span className="text-xs text-secondary">
                    {amount} {lot.symbol}
                  </span>
                </div>
              ) : (
                EMPTY_VALUE
              )
            }
          />
          <DetailRow
            label="Gain"
            value={
              gain != null ? (
                <CurrencyAmount
                  layout="standard"
                  size="small"
                  value={gain}
                  direction={directionForChange(gain)}
                />
              ) : (
                EMPTY_VALUE
              )
            }
          />
        </div>
      </LabelValue>
    </div>
  )
}

export const LotDetailDialog = ({ lot }: { lot: LotRow }) => {
  const { serverGetRequest } = useServerRequests()
  const { org } = useActiveOrg()
  const [tz] = useAccountingTimezone()
  const { getPrice, usePricesVersion } = useTokenPrices()
  // getPrice reads a cache and queues the fetch; the version subscription is
  // what re-renders this dialog once that fetch lands.
  usePricesVersion()

  const { data: disposals, isLoading } = useQuery({
    queryKey: [QUERY_KEYS.ORG_ACCOUNTING_LOT_DISPOSALS, org?.id, lot.id],
    queryFn: async () => {
      if (!org?.id) throw new Error('Org ID required')

      const response = await serverGetRequest<{ disposals: LotDisposal[] }>({
        path: `/api/org/accounting/lots/${lot.id}/disposals`,
        params: { orgId: org.id },
        errorMessage: 'Failed to fetch lot sales',
      })
      if (response) {
        return response.data.disposals
      }
    },
    enabled: !!org?.id,
  })

  const fraction = lot.quantity > 0 ? lot.quantityRemaining / lot.quantity : 0
  const formatAmount = (value: number) =>
    tokenBalanceFormatters.formatAbbreviatedTokenAmount(value, {
      address: lot.tokenAddress as IAddress,
      chainId: lot.chainId,
    })

  // Unrealized gain covers only what the lot still holds; the consumed part
  // has already booked its realized gain against each disposal below.
  const marketPrice = getPrice(
    lot.chainId as SupportedChainId,
    lot.tokenAddress,
  )
  const unrealized =
    marketPrice == null
      ? null
      : lot.quantityRemaining * (marketPrice - lot.unitPrice)

  return (
    <SplitsDialogContent
      id="lotDetail"
      title={`Lot ${lot.lotNumber}`}
      variant="info"
      maxWidth="md"
    >
      <div className="space-y-5">
        <div className="rounded-lg border border-gray-200 p-4 dark:border-gray-800">
          <div className="flex items-baseline justify-between gap-2">
            <div className="text-xs text-secondary">Lot remaining balance</div>
            <span className="shrink-0 text-xs text-secondary">
              {Math.round(fraction * 100)}%
            </span>
          </div>
          <div className="mt-1 min-w-0 truncate text-sm font-medium">
            {formatAmount(lot.quantityRemaining)}/{formatAmount(lot.quantity)}{' '}
            {lot.symbol}
          </div>
          <div className="mt-3 h-1.5 w-full overflow-hidden rounded-full bg-gray-200 dark:bg-gray-800">
            <div
              className="h-full rounded-full bg-green-600"
              style={{
                width: `${Math.min(100, Math.max(0, fraction * 100))}%`,
              }}
            />
          </div>
        </div>

        <div className="flex flex-col gap-3">
          <LabelValue
            size="small"
            label="Wallet"
            value={lot.smartAccountName}
          />
          <LabelValue
            size="small"
            label="From"
            value={
              lot.sourceAddress ? (
                <AddressDisplay
                  address={lot.sourceAddress as IAddress}
                  layout="standard"
                  size="small"
                />
              ) : (
                EMPTY_VALUE
              )
            }
          />
          <LabelValue
            size="small"
            label="Lot price"
            value={
              <span className="flex items-center gap-1">
                <CurrencyAmount
                  layout="standard"
                  size="small"
                  value={lot.unitPrice}
                />
                <span className="text-secondary">/ {lot.symbol}</span>
              </span>
            }
          />
          <LabelValue
            size="small"
            label="Cost basis"
            value={
              <CurrencyAmount
                layout="standard"
                size="small"
                value={lot.costBasis}
              />
            }
          />
          <LabelValue
            size="small"
            label="Market price"
            value={
              marketPrice != null ? (
                <span className="flex items-center gap-1">
                  <CurrencyAmount
                    layout="standard"
                    size="small"
                    value={marketPrice}
                  />
                  <span className="text-secondary">/ {lot.symbol}</span>
                </span>
              ) : (
                EMPTY_VALUE
              )
            }
          />
          <LabelValue
            size="small"
            label="Unrealized gain/loss"
            tooltip="Today's value of what's left in this lot, against what it cost"
            value={
              unrealized != null ? (
                <div className="flex flex-col items-end">
                  <CurrencyAmount
                    layout="standard"
                    size="small"
                    value={unrealized}
                    direction={directionForChange(unrealized)}
                  />
                  <span className="text-xs text-secondary">
                    on {formatAmount(lot.quantityRemaining)} {lot.symbol}
                  </span>
                </div>
              ) : (
                EMPTY_VALUE
              )
            }
          />
          <LabelValue
            size="small"
            // A carried lot keeps the date of the original acquisition, not of
            // the transfer that continued it, so the term stays correct.
            label="Acquired"
            tooltip={
              lot.originLotId
                ? 'Carried from an earlier lot, which is where this date comes from'
                : undefined
            }
            value={formatWithLocalTimezone(new Date(lot.acquisitionTime), {
              format: DATE_TIME_FORMAT,
              tz,
            })}
          />
          <LabelValue
            size="small"
            label="Held"
            value={`${lot.heldDays.toLocaleString()} ${lot.heldDays === 1 ? 'day' : 'days'}`}
          />
        </div>

        <div className="border-t border-gray-200 pt-4 dark:border-gray-800">
          <div className="mb-2 text-sm font-medium">Sales</div>
          {isLoading ? (
            <div className="flex min-h-16 items-center justify-center">
              <LoadingSpinner size="lg" thickness="normal" variant="default" />
            </div>
          ) : disposals?.length ? (
            <div className="max-h-80 divide-y divide-gray-200 overflow-y-auto rounded-lg border border-gray-200 dark:divide-gray-800 dark:border-gray-800">
              {disposals.map((disposal) => (
                <DisposalRow key={disposal.id} disposal={disposal} lot={lot} />
              ))}
            </div>
          ) : (
            <div className="text-sm text-secondary">
              Nothing sold from this lot yet
            </div>
          )}
        </div>

        <LotAssertionHistory lot={lot} />
      </div>
    </SplitsDialogContent>
  )
}
